// FCM 푸시 토큰 헬퍼 — 로그아웃 시 이 기기 토큰을 서버에서 파기(개인정보 처리방침 #3).
import { Capacitor } from "@capacitor/core";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { Preferences } from "@capacitor/preferences";
import { apiFetch } from "./api";

const PENDING_UNREGISTER_KEY = "pending_push_unregister_v1";

/**
 * 🔴 N-3: 파기에 실패한 토큰을 기기에 남긴다.
 *
 * 예전 주석은 실패를 삼키며 *"다음 발송 시 invalid-token 정리로도 회수됨"* 이라고 적어뒀는데
 * **사실이 아니다.** 그 정리는 FCM이 무효라고 답한 토큰만 지운다. 로그아웃한 기기의 토큰은
 * 여전히 **유효**하므로 회수되지 않고, 이전 계정의 알림이 계속 그 기기로 도착한다.
 *
 * ⚠ 한계(설계상 남는 구간): `/api/mobile/push/unregister`는 **인증이 필요하다**.
 *   로그아웃이 끝나면 세션이 없어 재시도할 수 없다. 그래서 이 큐는
 *   ① 세션이 아직 살아 있는 앱 시작 시점(같은 계정으로 다시 들어온 경우)에 비워지고,
 *   ② 다른 계정으로 로그인하면 register가 `ON CONFLICT (token)`으로 소유권을 가져가며 해소된다(N-4).
 *   "오프라인 로그아웃 후 아무도 로그인하지 않는 기간"은 서버가 알 방법이 없어 남는다.
 */
async function queuePendingUnregister(token: string): Promise<void> {
  try {
    const { value } = await Preferences.get({ key: PENDING_UNREGISTER_KEY });
    const list: string[] = value ? JSON.parse(value) : [];
    if (!list.includes(token)) list.push(token);
    await Preferences.set({ key: PENDING_UNREGISTER_KEY, value: JSON.stringify(list.slice(-5)) });
  } catch {
    /* 저장 실패까지 막을 방법은 없다 — 로그아웃을 막지는 않는다 */
  }
}

/** 보관된 파기 대기 토큰을 처리한다. 세션이 유효할 때만 성공하며, 성공한 것만 큐에서 뺀다. */
export async function flushPendingUnregister(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  let list: string[] = [];
  try {
    const { value } = await Preferences.get({ key: PENDING_UNREGISTER_KEY });
    list = value ? JSON.parse(value) : [];
  } catch {
    return;
  }
  if (!Array.isArray(list) || list.length === 0) return;

  const remaining: string[] = [];
  for (const token of list) {
    try {
      await apiFetch("/api/mobile/push/unregister", { method: "POST", body: { token } });
    } catch {
      remaining.push(token); // 아직 못 지웠다 — 다음 기회에
    }
  }
  try {
    if (remaining.length > 0) {
      await Preferences.set({ key: PENDING_UNREGISTER_KEY, value: JSON.stringify(remaining) });
    } else {
      await Preferences.remove({ key: PENDING_UNREGISTER_KEY });
    }
  } catch {
    /* noop */
  }
}

/**
 * 알림 권한을 (아직 안 물었으면) 요청하고, 허용이면 FCM 토큰을 서버에 등록한다.
 *
 * 🔴 2026-09-27: 권한 요청이 **첫 수동 토론**(PortfolioAnalysisScreen) 한 곳뿐이었다.
 *   자동 복기만 쓰는 사용자는 iOS가 권한을 물을 기회가 없어 설정>알림 목록에 솟다가 아예 없었고,
 *   서버는 토큰 0개라 푸시 없이 outbox를 sent로 닫았다 → 앱을 켜야만 토스트로 결과를 알았다.
 *   그래서 "푸시를 받는 기능을 켜는 지점"마다 이 함수를 부른다(토론 생성 · 자동 복기 켜기).
 *
 * - 'prompt'일 때만 시스템 다이얼로그를 띄운다. 이미 거부('denied')면 iOS는 다시 묻지 못한다.
 * - 개인정보 #4: 허용한 경우에만 토큰을 발급·등록한다(거부 시 등록 API 미호출).
 * - 실패는 삼킨다 — 권한 문제로 본 기능(토론·설정 저장)을 막지 않는다.
 * @returns 최종 권한 상태 (웹·실패 시 null)
 */
export async function ensurePushRegistered(): Promise<string | null> {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    let permission = await FirebaseMessaging.checkPermissions();
    if (permission.receive === "prompt" || permission.receive === "prompt-with-rationale") {
      permission = await FirebaseMessaging.requestPermissions();
    }
    if (permission.receive !== "granted") return permission.receive;
    const { token } = await FirebaseMessaging.getToken();
    if (token) {
      await apiFetch("/api/mobile/push/register", {
        method: "POST",
        body: { token, platform: Capacitor.getPlatform() },
      });
    }
    return permission.receive;
  } catch (error) {
    console.warn("[push] 알림 권한 또는 등록 처리 실패", error);
    return null;
  }
}

// 로그아웃 직전 호출 — best-effort. 실패해도 로그아웃은 진행한다(비차단).
export async function unregisterPushToken(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  let token: string | null = null;
  try {
    token = (await FirebaseMessaging.getToken())?.token || null;
  } catch {
    return; // 토큰 자체를 못 얻으면 지울 대상도 없다
  }
  if (!token) return;
  try {
    await apiFetch("/api/mobile/push/unregister", { method: "POST", body: { token } });
  } catch {
    // 오프라인·일시 오류 — 삼키지 않고 기기에 남긴다(위 주석의 한계 참조).
    await queuePendingUnregister(token);
  }
}
